import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import type { BoundaryGroupDemographic } from '~/store/boundaryGroups/boundaryGroups.repository';

export type ReportTemplateItem = ReportTemplate['items'][number];
export type ReportTemplateTableItem = Extract<ReportTemplateItem, { type: 'table' }>;
export type ReportTemplateTableRow = ReportTemplateTableItem['content'][number];
export type ReportTemplateDataRow = Extract<ReportTemplateTableRow, { type: 'data-row' }>;

export type DemographicOption = Readonly<{
  id: string;
  label: string;
  category: string;
  region: string;
}>;

export const createBlankReportTemplate = (): ReportTemplate => ({
  id: 'new-report-template',
  name: 'New Report Template',
  items: [
    { type: 'map' },
    {
      type: 'table',
      title: 'Demographics',
      content: [
        { type: 'title-row', content: 'Population' },
        { type: 'data-row', id: 'POPCY' },
      ],
    },
  ],
});

export const createTemplateItem = (type: ReportTemplateItem['type']): ReportTemplateItem => {
  switch (type) {
    case 'map':
      return { type: 'map' };
    case 'line-break':
      return { type: 'line-break' };
    case 'page-break':
      return { type: 'page-break' };
    case 'title':
      return { type: 'title', content: 'New Title' };
    case 'table':
      return { type: 'table', title: 'New Table', content: [] };
    default:
      throw new Error(`Unsupported template item type: ${type satisfies never}`);
  }
};

export const createTableRow = (type: ReportTemplateTableRow['type']): ReportTemplateTableRow => {
  switch (type) {
    case 'data-row':
      return { type: 'data-row', id: 'POPCY' };
    case 'title-row':
      return { type: 'title-row', content: 'New Section' };
    case 'empty-row':
      return { type: 'empty-row' };
    default:
      throw new Error(`Unsupported table row type: ${type satisfies never}`);
  }
};

export const moveArrayItem = <T>(items: ReadonlyArray<T>, fromIndex: number, toIndex: number): ReadonlyArray<T> => {
  if (toIndex < 0 || toIndex >= items.length || fromIndex === toIndex) {
    return items;
  }

  const nextItems = [...items];
  const [item] = nextItems.splice(fromIndex, 1);
  if (item !== undefined) {
    nextItems.splice(toIndex, 0, item);
  }

  return nextItems;
};

export const flattenDemographicOptions = (
  regions: ReadonlyArray<{ id: string; demographics: readonly BoundaryGroupDemographic[] }>
): ReadonlyArray<DemographicOption> => regions.flatMap(region => region.demographics.map(demographic => ({
  id: demographic.id,
  label: demographic.name,
  category: demographic.category,
  region: region.id,
})));

export const getDemographicLabelMap = (options: ReadonlyArray<DemographicOption>): ReadonlyMap<string, string> => {
  return new Map(options.flatMap(option => [
    [option.id, option.label] as const,
    [`${option.id}%`, `${option.label} %`] as const,
    [`%${option.id}`, `${option.label} %`] as const,
  ]));
};

export const getInvalidDemographicDataRows = (
  template: ReportTemplate,
  options: ReadonlyArray<DemographicOption>
): ReadonlyArray<{ itemIndex: number; rowIndex: number; id: string }> => {
  const demographicIds = new Set(options.flatMap(option => [option.id, `${option.id}%`]));
  const isValidId = (id: string) => demographicIds.has(id) || (id.startsWith('%') && demographicIds.has(id.slice(1)));

  return template.items.flatMap((item, itemIndex) => item.type === 'table'
    ? item.content.flatMap((row, rowIndex) => {
      if (row.type !== 'data-row' || row.formula || isValidId(row.id)) {
        return [];
      }

      return [{ itemIndex, rowIndex, id: row.id }];
    })
    : []);
};

export const filterDemographicOptions = (
  options: ReadonlyArray<DemographicOption>,
  search: string
): ReadonlyArray<DemographicOption> => {
  const normalizedSearch = search.replace(/^%/, '').trim().toLowerCase();
  if (!normalizedSearch) {
    return options.slice(0, 100);
  }

  return options.filter(option => {
    return option.id.toLowerCase().includes(normalizedSearch) ||
      option.label.toLowerCase().includes(normalizedSearch) ||
      option.category.toLowerCase().includes(normalizedSearch) ||
      option.region.toLowerCase().includes(normalizedSearch);
  }).slice(0, 100);
};

export const getTemplateFileName = (template: ReportTemplate): string => {
  const safeId = template.id.trim().replace(/[^a-z0-9_-]+/gi, '_') || 'report-template';

  return `${safeId}.rt.yml`;
};
